import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Building2, Check, Clock, CreditCard, Link2, Loader2, MapPin, Plus, RefreshCw, ShieldAlert, Store } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BranchPinDialog } from './BranchPinDialog';
import { ChainSetupCard } from '../components/ChainSetupCard';
import { CopyBranchSetupDialog } from '../components/CopyBranchSetupDialog';
import { CreateTransferDialog } from '../components/transfers/CreateTransferDialog';
import '../components/transfers/transfers.css';

const STATUS_STYLE = {
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  pending: 'border-amber-300 bg-amber-50 text-amber-900',
  info_requested: 'border-sky-300 bg-sky-50 text-sky-900',
  rejected: 'border-red-300 bg-red-50 text-red-800',
};
const STATUS_LABEL = {
  approved: 'Approved · live', pending: 'Pending approval',
  info_requested: 'More information needed', rejected: 'Rejected',
};
const blank = { name: '', town: '', address: '', phone: '', licence_reference: '', note: '' };

export const StatusPill = ({ value, testId }) => (
  <Badge variant="outline" className={`text-[11px] font-medium ${STATUS_STYLE[value] || STATUS_STYLE.pending}`} data-testid={testId}>
    {STATUS_LABEL[value] || value}
  </Badge>
);

export default function BranchesPage() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(blank);
  const [requesting, setRequesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resubmit, setResubmit] = useState(null);
  const [note, setNote] = useState('');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [pinning, setPinning] = useState(null);
  const [copying, setCopying] = useState(null);
  const [sending, setSending] = useState(null);

  const load = useCallback(() => {
    api.get('/branches').then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load your branches'));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Returning from Paystack: apply the slot before showing the page state.
  useEffect(() => {
    const reference = params.get('slot_reference');
    if (!reference) return;
    api.post('/branches/slots/verify', { reference })
      .then(r => {
        if (r.data.success) toast.success('Extra location added — you can request the branch now');
        else toast.error(r.data.message || 'Payment not confirmed yet');
      })
      .catch(() => toast.error('Could not confirm that payment'))
      .finally(() => { params.delete('slot_reference'); setParams(params, { replace: true }); load(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buySlot = async () => {
    setBusy(true);
    try {
      const callback = `${window.location.origin}/branches?slot_reference=`;
      const { data: link } = await api.post('/branches/slots/checkout',
        { quantity: 1, callback_url: callback });
      window.location.href = link.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the payment');
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/branches/requests', { ...form, note: form.note || '' });
      toast.success('Branch requested — DutiLyne will review it shortly');
      setRequesting(false);
      setForm(blank);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit the request'); }
    finally { setBusy(false); }
  };

  const sendResubmit = async () => {
    setBusy(true);
    try {
      await api.post(`/branches/${resubmit.tenant_id}/resubmit`, { note });
      toast.success('Sent back to DutiLyne for review');
      setResubmit(null); setNote(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not resubmit'); }
    finally { setBusy(false); }
  };

  const select = async (tenantId) => {
    try {
      await api.post(`/tenants/${tenantId}/select`);
      toast.success('Branch selected');
      if (refreshUser) refreshUser();
      load();
    } catch { toast.error('Could not switch branch'); }
  };

  const copyRemoteLink = async (tenantId) => {
    try {
      const { data: link } = await api.get(`/branches/${tenantId}/remote-link`);
      const url = `${window.location.origin}${link.path}`;
      await navigator.clipboard.writeText(url);
      toast.success('Remote-access link copied — the owner gets full access, staff get read-only');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create a remote-access link'); }
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const { plan } = data;
  const atLimit = plan.limit && plan.used >= plan.limit;
  const canRequest = ['super_admin', 'admin'].includes(user?.role);
  const planned = data.pharmacy.planned_branches || 1;
  const showChain = planned > 1 && (data.branches.length < planned || params.get('setup') === 'chain');

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Store className="h-7 w-7 text-primary" /> Branches
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="branches-pharmacy-name">
            Locations of {data.pharmacy.name} · {plan.used} of {plan.limit} on {plan.tier_name}
            {plan.extra_slots ? ` (incl. ${plan.extra_slots} paid)` : ''}
            {data.pharmacy.publicly_listed ? ' · listed publicly' : ' · not listed publicly yet'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={load} data-testid="branches-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          {canRequest && atLimit && (
            <Button variant="outline" className="gap-2" onClick={buySlot} disabled={busy} data-testid="buy-branch-slot-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Add a location · GHS {plan.slot_price_ghs}/month
            </Button>
          )}
          {canRequest && (
            <Button className="gap-2" onClick={() => setRequesting(true)} disabled={atLimit} data-testid="request-branch-btn">
              <Plus className="h-4 w-4" /> Request a new branch
            </Button>
          )}
        </div>
      </div>

      {atLimit && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="branch-limit-notice">
          <CardContent className="py-4 text-sm text-amber-900 flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            Your {plan.tier_name} plan covers {plan.plan_branches} location{plan.plan_branches === 1 ? '' : 's'}
            {plan.extra_slots ? ` plus ${plan.extra_slots} paid slot${plan.extra_slots === 1 ? '' : 's'}` : ''}, and all
            {' '}{plan.limit} are in use. Add a location for GHS {plan.slot_price_ghs} a month — it renews with your
            subscription and the new branch still goes to DutiLyne for approval.
          </CardContent>
        </Card>
      )}

      {showChain && (
        <ChainSetupCard key={planned} planned={planned} added={data.branches.length} plan={plan} canEdit={canRequest}
          onAdd={() => (atLimit ? buySlot() : setRequesting(true))} onChanged={load} />
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.branches.map(branch => (
          <Card key={branch.tenant_id} className="relative" data-testid={`branch-card-${branch.tenant_id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-start justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary shrink-0" />
                  <span className="break-words">{branch.name}</span>
                </span>
                <StatusPill value={branch.approval_status} testId={`branch-status-${branch.tenant_id}`} />
              </CardTitle>
              <CardDescription className="flex items-center gap-1 text-xs">
                <MapPin className="h-3 w-3" /> {[branch.town, branch.address].filter(Boolean).join(' · ') || 'No address on file'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-muted-foreground">
              <div className="flex flex-wrap gap-2">
                {branch.is_head_location && <Badge variant="secondary" className="text-[10px]">Head location</Badge>}
                {branch.portal_visible
                  ? <Badge variant="outline" className="text-[10px] gap-1"><Check className="h-3 w-3" /> On the customer portal</Badge>
                  : <Badge variant="outline" className="text-[10px] gap-1"><Clock className="h-3 w-3" /> Not public yet</Badge>}
                <Badge variant="outline" className="text-[10px]">{branch.user_count} staff</Badge>
                <Badge variant="outline" className="text-[10px]">{branch.product_count} products</Badge>
                {branch.latitude
                  ? <Badge variant="outline" className="text-[10px]">On the map</Badge>
                  : <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">No map pin</Badge>}
              </div>
              {branch.approval_reason && (
                <p className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1.5 text-[11px] text-sky-900" data-testid={`branch-reason-${branch.tenant_id}`}>
                  DutiLyne: “{branch.approval_reason}”
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => select(branch.tenant_id)}
                  disabled={data.selected_tenant_id === branch.tenant_id} data-testid={`branch-select-${branch.tenant_id}`}>
                  {data.selected_tenant_id === branch.tenant_id ? 'Currently selected' : 'Work in this branch'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPinning(branch)}
                  data-testid={`branch-pin-${branch.tenant_id}`}>
                  {branch.latitude ? 'Adjust map pin' : 'Place map pin'}
                </Button>
                {canRequest && branch.approval_status === 'approved' && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => copyRemoteLink(branch.tenant_id)}
                    data-testid={`branch-remote-link-${branch.tenant_id}`}>
                    <Link2 className="h-3.5 w-3.5" /> Copy remote link
                  </Button>
                )}
                {canRequest && !branch.is_head_location && branch.is_active && (
                  <Button size="sm" variant="outline" onClick={() => setSending(branch)}
                    data-testid={`branch-send-stock-${branch.tenant_id}`}>Send stock</Button>
                )}
                {canRequest && !branch.is_head_location && (
                  <Button size="sm" variant="outline" onClick={() => setCopying(branch)}
                    data-testid={`branch-copy-setup-${branch.tenant_id}`}>Copy head-branch setup</Button>
                )}
                {canRequest && ['rejected', 'info_requested'].includes(branch.approval_status) && (
                  <Button size="sm" onClick={() => { setResubmit(branch); setNote(''); }}
                    data-testid={`branch-resubmit-${branch.tenant_id}`}>Resubmit</Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {pinning && (
        <BranchPinDialog branch={pinning} onClose={() => setPinning(null)} onSaved={load} />
      )}
      <CreateTransferDialog open={Boolean(sending)} onOpenChange={v => !v && setSending(null)} initialDestination={sending?.tenant_id || ''}
        branchKey={sending?.tenant_id || 'none'} onCreated={() => navigate('/stock-transfers')} />
      {copying && (
        <CopyBranchSetupDialog branch={copying} onClose={() => setCopying(null)} onDone={load} />
      )}

      <Dialog open={requesting} onOpenChange={setRequesting}>
        <DialogContent className="max-w-lg" data-testid="request-branch-dialog">
          <DialogHeader>
            <DialogTitle>Request a new branch</DialogTitle>
            <DialogDescription>
              You can prepare the branch — staff, stock, pricing — while DutiLyne reviews it. It appears
              to customers once approved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[['name', 'Branch name', 'Osu branch'], ['town', 'Town', 'Accra'],
              ['address', 'Street address', '12 Oxford Street'], ['phone', 'Branch phone', '0244 000 000'],
              ['licence_reference', 'Premises licence / reference', 'PC-PREM-2026-0912']].map(([key, label, placeholder]) => (
              <div className="space-y-1.5" key={key}>
                <Label className="text-xs">{label}{key === 'licence_reference' ? '' : ' *'}</Label>
                <Input value={form[key]} placeholder={placeholder}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  data-testid={`branch-field-${key.replace(/_/g, '-')}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs">Anything DutiLyne should know</Label>
              <Textarea rows={2} value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                data-testid="branch-field-note" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRequesting(false)} data-testid="branch-request-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.name.trim() || !form.town.trim() || !form.address.trim() || form.phone.trim().length < 7}
              data-testid="branch-request-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Submit request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resubmit} onOpenChange={v => !v && setResubmit(null)}>
        <DialogContent className="max-w-md" data-testid="branch-resubmit-dialog">
          <DialogHeader>
            <DialogTitle>Resubmit {resubmit?.name}</DialogTitle>
            <DialogDescription>{resubmit?.approval_reason || 'Tell DutiLyne what changed.'}</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
            placeholder="What has changed since the last review" data-testid="branch-resubmit-note" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setResubmit(null)}>Cancel</Button>
            <Button onClick={sendResubmit} disabled={busy || note.trim().length < 5} data-testid="branch-resubmit-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Send for review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
